import { useState } from 'react'
import type { VulnCpe } from '@/lib/types'

export interface AffectedProductsCardProps {
  cpes: VulnCpe[]
}

interface VendorProductGroup {
  vendor: string
  product: string
  versions: string[] // non-null, sorted ascending
}

function groupByVendorProduct(cpes: VulnCpe[]): VendorProductGroup[] {
  const map = new Map<string, VendorProductGroup>()
  for (const c of cpes) {
    const key = `${c.vendor}\u0000${c.product}`
    let g = map.get(key)
    if (!g) {
      g = { vendor: c.vendor, product: c.product, versions: [] }
      map.set(key, g)
    }
    if (c.version && !g.versions.includes(c.version)) {
      g.versions.push(c.version)
    }
  }
  for (const g of map.values()) g.versions.sort()
  return Array.from(map.values()).sort((a, b) =>
    `${a.vendor}/${a.product}`.localeCompare(`${b.vendor}/${b.product}`),
  )
}

function renderVersions(versions: string[]): string {
  if (versions.length === 0) return 'any version'
  if (versions.length === 1) return versions[0]!
  // For 2+ versions we show a range spanning the sorted first → last.
  // This matches the common CPE reporting convention and avoids noisy commas.
  return `${versions[0]} – ${versions[versions.length - 1]}`
}

/**
 * V6 — Card 2: Affected Products.
 *
 * Lists vendor/product/version tuples from the CIRCL CPE enrichment. Groups
 * by (vendor, product) so multiple versions of the same product collapse
 * into a single row. "View all CPEs" toggles the raw CPE 2.3 strings in
 * place — no modal — so operators can copy/paste into their own tooling.
 *
 * Hidden when the CPE list is empty.
 */
export function AffectedProductsCard({ cpes }: AffectedProductsCardProps) {
  const [expanded, setExpanded] = useState(false)
  if (cpes.length === 0) return null

  const groups = groupByVendorProduct(cpes)
  const totalVersions = groups.reduce(
    (acc, g) => acc + Math.max(1, g.versions.length),
    0,
  )

  return (
    <section
      data-testid="affected-products-card"
      className="rounded-lg border border-border bg-card p-4 my-4"
      aria-label="Affected Products"
    >
      <h3 className="text-sm font-semibold">Affected Products</h3>
      <ul className="mt-2 space-y-2">
        {groups.map((g) => (
          <li key={`${g.vendor}/${g.product}`}>
            <div className="font-medium">
              {g.vendor}
              {g.product && ` — ${g.product}`}
            </div>
            <div className="text-sm text-muted-foreground">
              {renderVersions(g.versions)}
            </div>
          </li>
        ))}
      </ul>
      <p className="text-xs text-muted-foreground mt-3">
        {groups.length} product{groups.length === 1 ? '' : 's'}
        {' · '}
        {totalVersions} version{totalVersions === 1 ? '' : 's'}
      </p>
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className="text-xs text-primary hover:underline mt-2"
        aria-expanded={expanded}
      >
        {expanded ? 'Hide all CPEs' : 'View all CPEs ↗'}
      </button>
      {expanded && (
        <ul className="mt-2 space-y-1 text-xs font-mono text-muted-foreground break-all">
          {cpes.map((c) => (
            <li key={c.id}>{c.cpe}</li>
          ))}
        </ul>
      )}
    </section>
  )
}
